import { Card, Divider, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { BillingDetail } from './hooks/useBilling';
import { formatPenceToPounds, getBillingTypeLabel } from './billingUtils';
import { StatRow } from './StatRow';
import { TotalRow } from './TotalRow';
import { BillSummaryRow } from './BillSummaryRow';
import { WarnRow } from './WarnRow';

type BillingDetailModalProps = {
  billingDetail: BillingDetail | null;
  onClose: () => void;
};

export default function BillingDetailModal({ billingDetail, onClose }: BillingDetailModalProps) {
  if (!billingDetail) return null;

  const isFixed = billingDetail.billingType === 'FIXED';

  return (
    <Modal opened={!!billingDetail} onClose={onClose} title={billingDetail.companyName} size={1100}>
      <Group align="flex-start" gap="xl" wrap="nowrap">
        <div style={{ flex: 7 }}>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Client & family stats
            </Text>

            <StatRow
              label="Advised clients (without family)"
              value={billingDetail.advisedClientsWithoutFamily}
            />
            <StatRow label="Advised families" value={billingDetail.advisedFamiliesCount} />
            <TotalRow label="Billable units" value={billingDetail.billableUnitCount} />
            <Card p="xs" bg="yellow.0" radius="sm">
              <Stack gap={4}>
                <WarnRow
                  label="Non-advised clients"
                  value={`${billingDetail.nonAdvisedClientsCount} (${formatRatio(billingDetail.nonAdvisedClientRatio)})`}
                  warn={billingDetail.nonAdvisedClientRatio > 0.05}
                  hint="Exceeds 5% threshold"
                />
                <WarnRow
                  label="Non-advised families"
                  value={`${billingDetail.nonAdvisedOnlyFamiliesCount} (${formatRatio(billingDetail.nonAdvisedFamilyRatio)})`}
                  warn={billingDetail.nonAdvisedOnlyFamiliesCount > 0}
                  hint="Families with only non-advised members shouldn't exist"
                />
              </Stack>
            </Card>

            <Divider />

            <Text fw={600} size="sm">
              Engagement
            </Text>

            <StatRow
              label="Engaged advised clients"
              value={billingDetail.engagedAdvisedClientsCount}
            />
            <StatRow label="Engaged advised families" value={billingDetail.engagedFamiliesCount} />
            <TotalRow
              label="Engaged billable units"
              value={billingDetail.engagedBillableUnitCount}
            />
            <Card p="xs" bg="yellow.0" radius="sm">
              <Stack gap={4}>
                <WarnRow
                  label="Engaged non-advised clients"
                  value={billingDetail.nonAdvisedEngagedCount}
                  warn={billingDetail.nonAdvisedEngagedCount > 0}
                  hint="Non-advised clients shouldn't be engaged"
                />
                <WarnRow
                  label="Engaged non-advised families"
                  value={billingDetail.engagedNonAdvisedFamiliesCount}
                  warn={billingDetail.engagedNonAdvisedFamiliesCount > 0}
                  hint="Non-advised-only families shouldn't be engaged"
                />
              </Stack>
            </Card>
          </Stack>
        </div>
        <Divider orientation="vertical" />
        <div style={{ flex: 5 }}>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Billing
            </Text>

            <StatRow label="Type" value={getBillingTypeLabel(billingDetail.billingType)} />
            <StatRow label="Start date" value={formatStartDate(billingDetail.billingStartDate)} />
            {isFixed ? (
              <TotalRow label="Total amount" value={formatAmount(billingDetail.billingAmount)} />
            ) : (
              <StatRow label="Price per unit" value={formatAmount(billingDetail.billingAmount)} />
            )}
            {billingDetail.minimumBillingAmount != null && (
              <StatRow
                label="Minimum amount"
                value={formatAmount(billingDetail.minimumBillingAmount)}
              />
            )}
            {!isFixed && (
              <BillSummaryRow
                label={
                  billingDetail.billOnlyEngaged ? 'Engaged monthly bill' : 'Advised monthly bill'
                }
                units={
                  billingDetail.billOnlyEngaged
                    ? billingDetail.engagedBillableUnitCount
                    : billingDetail.billableUnitCount
                }
                bill={formatPenceToPounds(
                  billingDetail.billOnlyEngaged
                    ? billingDetail.engagedMonthlyBillPence
                    : billingDetail.monthlyBillPence,
                )}
                calculatedBill={calculateBillBeforeMinimum(
                  billingDetail.billingAmount,
                  billingDetail.billOnlyEngaged
                    ? billingDetail.engagedBillableUnitCount
                    : billingDetail.billableUnitCount,
                )}
                isMinApplied={isMinApplied(
                  billingDetail.billOnlyEngaged
                    ? billingDetail.engagedMonthlyBillPence
                    : billingDetail.monthlyBillPence,
                  billingDetail.minimumBillingAmount,
                )}
              />
            )}
            {billingDetail.hasWarnings && (
              <Card p="sm" bg="red.0" radius="sm" mt="xs">
                <Group gap="xs">
                  <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
                  <Text size="sm" fw={600} c="red.7">
                    Anomalies detected — review the details on the left
                  </Text>
                </Group>
              </Card>
            )}
          </Stack>
        </div>
      </Group>
    </Modal>
  );
}

function calculateBillBeforeMinimum(
  billingAmount: number | null | undefined,
  units: number,
): string | null {
  if (billingAmount == null) return null;
  const amount = Number(billingAmount);
  if (Number.isNaN(amount)) return null;
  const pence = Math.floor(amount * units * 100);
  return formatPenceToPounds(pence);
}

function isMinApplied(billPence: number | null, minAmount: number | null | undefined): boolean {
  if (billPence == null || minAmount == null) return false;
  return billPence === Math.floor(Number(minAmount) * 100);
}

function formatStartDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function formatRatio(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

function formatAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '-';
  return `£${Number(amount).toFixed(2)}`;
}
